import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { components as c } from "@/components";
import { searchForTemplateAiWizardNavigation } from "@/features/ai-builder/useTemplateAiDraftStore";
import { TemplateScreenerSelect } from "@/features/screeners/components/TemplateScreenerSelect";
import { TemplateStepFooter } from "@/features/templates/components/TemplateStepFooter";
import { useFinalizeTemplateWizard } from "@/features/templates/hooks/useFinalizeTemplateWizard";
import { useTemplateNavigationStore } from "@/features/templates/hooks/useTemplateNavigationStore";
import { useAuth } from "@/utils/auth";
import { templateSettingsSchema, TrackingType, type TemplateSettingsSchemaT } from "@patient-watch/types";

/** Admin-only first step: the eligibility questions patients answer before this template's sign-up link continues. */
const TemplateScreenerStep = () => {
    const auth = useAuth();
    const navigate = useNavigate();
    const { templateId } = Route.useParams();
    const search = Route.useSearch();
    const [screenerId, setScreenerId] = useState<string | null>(search.screener_id ?? null);
    const { currentStep, setCurrentStep, markStepComplete, nextStep, updateEventSteps, getNextStepRoute } =
        useTemplateNavigationStore();
    const { finalize, isSubmitting, isTemplateOwnerLookupBlockingSubmit } = useFinalizeTemplateWizard(
        templateId,
        search as Partial<TemplateSettingsSchemaT>
    );

    const effectiveEvents = useMemo(
        () => (search.required_events ?? []).filter((event) => event !== "skip") as TrackingType[],
        [search.required_events]
    );

    // A reload lands here with only the base steps in the store, so rebuild the steps that follow.
    useEffect(() => {
        updateEventSteps(effectiveEvents);
        setCurrentStep("screener");
    }, [effectiveEvents, updateEventSteps, setCurrentStep]);

    const goNext = () => {
        const nextSettings = { ...search, screener_id: screenerId } as Partial<TemplateSettingsSchemaT>;
        const route = getNextStepRoute(currentStep, { templateId });
        if (!route) {
            finalize(nextSettings);
            return;
        }
        const payload = searchForTemplateAiWizardNavigation(templateId, auth?.id ?? "", nextSettings);
        if (!payload) return;
        markStepComplete("screener");
        nextStep();
        navigate({ ...route, search: payload as never });
    };

    return (
        <div className="space-y-8">
            <c.ui.Card>
                <c.ui.CardHeader>
                    <c.ui.CardTitle>Screener</c.ui.CardTitle>
                    <c.ui.CardDescription>
                        Patients answer these eligibility questions before they can sign up from this template&apos;s
                        link or QR code. To make one, open a form in the{" "}
                        <Link to="/builder/forms" className="underline underline-offset-2">
                            Form builder
                        </Link>{" "}
                        and use its Screener step.
                    </c.ui.CardDescription>
                </c.ui.CardHeader>
                <c.ui.CardContent>
                    <TemplateScreenerSelect value={screenerId} onChange={setScreenerId} />
                </c.ui.CardContent>
            </c.ui.Card>

            <TemplateStepFooter
                onPrimaryClick={goNext}
                isLoadingPrimary={isSubmitting}
                disabledPrimary={isTemplateOwnerLookupBlockingSubmit}
            />
        </div>
    );
};

const screenerSearchSchema = templateSettingsSchema.partial().extend({
    required_events: z.array(z.union([TrackingType, z.literal("skip")])).optional(),
});

export const Route = createFileRoute("/_app/templates/$templateId/screener")({
    component: TemplateScreenerStep,
    validateSearch: (search) => screenerSearchSchema.parse(search),
});
